import * as Y from 'yjs';
import type { Command } from './types';

/** Shared store for the pending color to apply via style.setColor. */
let pendingColor: string | null = null;

/** Set the color that will be applied by the next style.setColor execution. */
export function setPendingColor(color: string): void {
  pendingColor = color;
}

/** Shared store for the pending font size to apply via style.setFontSize. */
let pendingFontSize: number | null = null;

/** Set the font size that will be applied by the next style.setFontSize execution. */
export function setPendingFontSize(size: number): void {
  pendingFontSize = size;
}

/** Shared store for the pending text color to apply via style.setTextColor. */
let pendingTextColor: string | null = null;

/** Set the text color that will be applied by the next style.setTextColor execution. */
export function setPendingTextColor(color: string): void {
  pendingTextColor = color;
}

function selectedNodeIds(ctx: { editorState: { selectedNodeIds: string[] } }): string[] {
  return ctx.editorState.selectedNodeIds;
}

function getYNodeStyle(doc: Y.Doc, nodeId: string): Y.Map<unknown> | undefined {
  const nodesMap = doc.getMap('nodes') as Y.Map<Y.Map<unknown>>;
  const yNode = nodesMap.get(nodeId);
  if (!yNode) return undefined;

  let yStyle = yNode.get('style') as Y.Map<unknown> | undefined;
  if (!yStyle) {
    yStyle = new Y.Map<unknown>();
    yNode.set('style', yStyle);
  }
  return yStyle;
}

export const styleCommands: Command[] = [
  {
    id: 'style.bold',
    label: 'Toggle Bold',
    category: 'node',
    canExecute: (ctx) => selectedNodeIds(ctx).length > 0,
    execute: (ctx) => {
      const nodeIds = selectedNodeIds(ctx);
      if (nodeIds.length === 0) return;
      ctx.doc.doc.transact(() => {
        for (const nodeId of nodeIds) {
          const yStyle = getYNodeStyle(ctx.doc.doc, nodeId);
          if (!yStyle) continue;
          const current = yStyle.get('fontWeight') as string | undefined;
          yStyle.set('fontWeight', current === 'bold' ? 'normal' : 'bold');
        }
      });
    },
  },
  {
    id: 'style.increaseFontSize',
    label: 'Increase Font Size',
    category: 'node',
    canExecute: (ctx) => selectedNodeIds(ctx).length > 0,
    execute: (ctx) => {
      const nodeIds = selectedNodeIds(ctx);
      if (nodeIds.length === 0) return;
      ctx.doc.doc.transact(() => {
        for (const nodeId of nodeIds) {
          const yStyle = getYNodeStyle(ctx.doc.doc, nodeId);
          if (!yStyle) continue;
          const current = (yStyle.get('fontSize') as number | undefined) ?? 14;
          yStyle.set('fontSize', Math.min(current + 2, 32));
        }
      });
    },
  },
  {
    id: 'style.decreaseFontSize',
    label: 'Decrease Font Size',
    category: 'node',
    canExecute: (ctx) => selectedNodeIds(ctx).length > 0,
    execute: (ctx) => {
      const nodeIds = selectedNodeIds(ctx);
      if (nodeIds.length === 0) return;
      ctx.doc.doc.transact(() => {
        for (const nodeId of nodeIds) {
          const yStyle = getYNodeStyle(ctx.doc.doc, nodeId);
          if (!yStyle) continue;
          const current = (yStyle.get('fontSize') as number | undefined) ?? 14;
          yStyle.set('fontSize', Math.max(current - 2, 10));
        }
      });
    },
  },
  {
    id: 'style.setFontSize',
    label: 'Set Font Size',
    category: 'node',
    canExecute: (ctx) => selectedNodeIds(ctx).length > 0 && pendingFontSize !== null,
    execute: (ctx) => {
      const nodeIds = selectedNodeIds(ctx);
      const size = pendingFontSize;
      if (nodeIds.length === 0 || !size) return;
      pendingFontSize = null;
      ctx.doc.doc.transact(() => {
        for (const nodeId of nodeIds) {
          const yStyle = getYNodeStyle(ctx.doc.doc, nodeId);
          if (!yStyle) continue;
          yStyle.set('fontSize', Math.max(8, Math.min(72, size)));
        }
      });
    },
  },
  {
    id: 'style.setColor',
    label: 'Set Node Color',
    category: 'node',
    canExecute: (ctx) => selectedNodeIds(ctx).length > 0 && pendingColor !== null,
    execute: (ctx) => {
      const nodeIds = selectedNodeIds(ctx);
      const color = pendingColor;
      if (nodeIds.length === 0 || !color) return;
      pendingColor = null;
      ctx.doc.doc.transact(() => {
        for (const nodeId of nodeIds) {
          const yStyle = getYNodeStyle(ctx.doc.doc, nodeId);
          if (!yStyle) continue;
          yStyle.set('backgroundColor', color);
        }
      });
    },
  },
  {
    id: 'style.toggleItalic',
    label: 'Toggle Italic',
    category: 'node',
    canExecute: (ctx) => selectedNodeIds(ctx).length > 0,
    execute: (ctx) => {
      const nodeIds = selectedNodeIds(ctx);
      if (nodeIds.length === 0) return;
      ctx.doc.doc.transact(() => {
        for (const nodeId of nodeIds) {
          const yStyle = getYNodeStyle(ctx.doc.doc, nodeId);
          if (!yStyle) continue;
          const current = yStyle.get('fontStyle') as string | undefined;
          yStyle.set('fontStyle', current === 'italic' ? 'normal' : 'italic');
        }
      });
    },
  },
  {
    id: 'style.toggleUnderline',
    label: 'Toggle Underline',
    category: 'node',
    canExecute: (ctx) => selectedNodeIds(ctx).length > 0,
    execute: (ctx) => {
      const nodeIds = selectedNodeIds(ctx);
      if (nodeIds.length === 0) return;
      ctx.doc.doc.transact(() => {
        for (const nodeId of nodeIds) {
          const yStyle = getYNodeStyle(ctx.doc.doc, nodeId);
          if (!yStyle) continue;
          const current = yStyle.get('textDecoration') as string | undefined;
          yStyle.set('textDecoration', current === 'underline' ? 'none' : 'underline');
        }
      });
    },
  },
  {
    id: 'style.toggleStrikethrough',
    label: 'Toggle Strikethrough',
    category: 'node',
    canExecute: (ctx) => selectedNodeIds(ctx).length > 0,
    execute: (ctx) => {
      const nodeIds = selectedNodeIds(ctx);
      if (nodeIds.length === 0) return;
      ctx.doc.doc.transact(() => {
        for (const nodeId of nodeIds) {
          const yStyle = getYNodeStyle(ctx.doc.doc, nodeId);
          if (!yStyle) continue;
          const current = yStyle.get('textDecoration') as string | undefined;
          yStyle.set('textDecoration', current === 'line-through' ? 'none' : 'line-through');
        }
      });
    },
  },
  {
    id: 'style.setTextColor',
    label: 'Set Text Color',
    category: 'node',
    canExecute: (ctx) => selectedNodeIds(ctx).length > 0 && pendingTextColor !== null,
    execute: (ctx) => {
      const nodeIds = selectedNodeIds(ctx);
      const color = pendingTextColor;
      if (nodeIds.length === 0 || !color) return;
      pendingTextColor = null;
      ctx.doc.doc.transact(() => {
        for (const nodeId of nodeIds) {
          const yStyle = getYNodeStyle(ctx.doc.doc, nodeId);
          if (!yStyle) continue;
          yStyle.set('textColor', color);
        }
      });
    },
  },
];
